import { useState } from "react";
import api from "@services/api";
import toast from "react-hot-toast";
import Modal from "@components/utils/Modal";
import { setOrganization } from "@redux/auth/actions";
import { useTranslation } from "react-i18next";
import FileInput from "../../components/utils/FileInput";
import { Mixpanel } from "../../services/mixpanel";

import LoadingButton from "../../components/loadingButton";
import { MIXPANEL_EVENTS } from "../../utils/mixpanelEvents";
import { useDispatch, useSelector } from "react-redux";
import { RxCross1 } from "react-icons/rx";
import { MdDeleteOutline } from "react-icons/md";
import { FaRegFile } from "react-icons/fa";
import { AiFillFileImage } from "react-icons/ai";
import GenerateEmailAddress from "../../components/modals/generateEmailAddress";
import { PRICING_FEATURES } from "../../constants";

const CreatePurchaseInvoice = ({ onCreated }) => {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);

  return (
    <div>
      <button
        className="btn-primary focus:bg-blue float-right"
        onClick={() => {
          setOpen(true);
          Mixpanel.track(MIXPANEL_EVENTS.btn_open_purchase_invoice_create_modal);
        }}>
        {t("purchase_invoices.create")}
      </button>
      <Modal isOpen={open} onClose={() => setOpen(false)} innerClassName="flex max-w-[50rem] w-[90vw] max-h-[calc(100vh-5rem)]">
        <div className="w-full max-h-full flex flex-col overflow-y-hidden transform rounded-lg bg-white text-left align-middle shadow-xl transition-all">
          <div className="px-8 pt-8 pb-3 border-b flex justify-between">
            <h2 className="text-2xl font-semibold text-gray-800">{t("purchase_invoices.create")}</h2>
            <button type="submit" onClick={() => setOpen(false)}>
              <RxCross1 />
            </button>
          </div>
          <div className="p-6 overflow-auto">
            <div className="flex flex-col row-start-1">
              <Form setOpen={setOpen} onCreated={onCreated} />
            </div>
          </div>
        </div>
      </Modal>
    </div>
  );
};

const Form = ({ setOpen, onCreated }) => {
  const [loading, setLoading] = useState(false);
  const { t } = useTranslation();
  const [loadingMessage, setLoadingMessage] = useState();
  const [values, setValues] = useState({});
  const { organization } = useSelector((state) => state.Auth);
  const [openGenerateEmailAddress, setOpenGenerateEmailAddress] = useState(false);
  const dispatch = useDispatch();

  const handleSubmit = async (event) => {
    setLoading(true);
    setLoadingMessage(t("purchase_invoices.loading_message_create"));
    try {
      event.preventDefault();
      const { data } = await api.post("/purchase-invoice", {
        OrganizationId: organization._id,
        reference: decodeURIComponent(values.file.split("/").pop().split(".").shift()),
        file: values?.file,
      });
      setLoadingMessage(t("purchase_invoices.loading_message_ocr"));
      const responseOcr = await api.post(`/purchase-invoice/${data._id}/ocr`, { OrganizationId: organization._id });

      await api.post(`/purchase-invoice/${data._id}/sync-transaction`, { OrganizationId: organization._id });

      toast.success(t("toast.sucess.created"));
      setLoadingMessage("");
      setLoading(false);
      setOpen(false);
      onCreated(responseOcr.data);
      setValues();
    } catch (e) {
      console.error(t("toast.error"), e);
      toast.error(e.code);
    }
  };

  const generateEmailAddress = async () => {
    try {
      if (!PRICING_FEATURES[organization?.plan]?.emailImport) return;
      const generatedEmail = organization.name
        .toLowerCase()
        .split(" ")
        .join("-")
        .replace(/[^a-z0-9-]/g, "");

      const { ok, data } = await api.put(`/organization/${organization._id}`, {
        expensesEmail: `${generatedEmail}@expenses.finotor.com`,
      });
      if (!ok) return toast.error(t("toast.error.created"));
      dispatch(setOrganization({ ...organization, expensesEmail: data.expensesEmail }));
    } catch (e) {
      console.error(t("toast.error"), e);
      toast.error(e.code);
    }
  };

  return (
    <form className="flex flex-col gap-8" onSubmit={handleSubmit}>
      {/* Section Transfert par email */}
      <div className="w-full">
        <div className="flex items-center gap-3 mb-4">
          <h2 className="text-xl font-bold text-gray-900">{t("purchase_invoices.import_by_email_title")}</h2>
          <span className="bg-blue-500 text-white text-xs font-medium px-2.5 py-1 rounded-full">{t("new")}</span>
        </div>
        <p className="text-gray-600 mb-5">{t("purchase_invoices.import_by_email_description")}</p>
        <div className="flex justify-center">
          {organization?.expensesEmail ? (
            <div className="flex items-center justify-between gap-2 w-1/2">
              <div className="w-full flex justify-between items-center border border-gray-300 rounded-lg pl-2 py-2">
                <input type="text" value={organization.expensesEmail} readOnly className="w-full bg-transparent focus:outline-none" />
              </div>
              <button
                type="button"
                onClick={() => {
                  navigator.clipboard.writeText(organization.expensesEmail);
                  toast.success(t("generate_email_modal.link_copied"));
                }}
                className="btn-primary px-6 flex items-center gap-2">
                {t("generate_email_modal.copy_link")}
              </button>
            </div>
          ) : (
            <button
              className="bg-white border border-gray-700 text-gray-700 px-6 py-2.5 text-sm rounded-lg hover:bg-gray-100 transition-colors font-medium"
              type="button"
              onClick={() => {
                generateEmailAddress();
                setOpenGenerateEmailAddress(true);
                Mixpanel.track(MIXPANEL_EVENTS.btn_open_generate_email_address_modal);
              }}>
              {t("purchase_invoices.import_by_email_btn")}
            </button>
          )}
        </div>
      </div>

      {/* Séparateur "ou" */}
      <div className="relative flex items-center justify-center">
        <div className="absolute inset-0 flex items-center">
          <div className="w-full border-t border-gray-200"></div>
        </div>
        <div className="relative bg-white px-4">
          <span className="text-gray-500 text-sm font-medium">{t("or")}</span>
        </div>
      </div>

      {/* Section Import de facture */}
      <div className="w-full">
        <h2 className="text-xl font-bold text-gray-900 mb-4">{t("purchase_invoices.import")}</h2>

        {values?.file ? (
          <div className="border-2 border-gray-200 rounded-xl p-6 bg-gray-50">
            <div className="flex justify-between items-start mb-3">
              <div className="flex items-center gap-3">
                <div className="text-gray-500">{values.file.indexOf(".pdf") !== -1 ? <FaRegFile size={24} /> : <AiFillFileImage size={24} />}</div>
                <a href={values.file} target="_blank" rel="noopener noreferrer" className="font-semibold text-gray-900 hover:underline">
                  {decodeURIComponent(values.file.split("/").pop())}
                </a>
              </div>
              <button type="button" onClick={() => setValues({ ...values, file: null })} className="text-gray-400 hover:text-red-500 transition-colors">
                <MdDeleteOutline size={20} />
              </button>
            </div>
          </div>
        ) : (
          <div className="flex gap-3 w-full">
            <FileInput onChange={(e) => setValues({ ...values, file: e.target.value })} name="{transaction._id}" folder="purchaseInvoiceTransaction" width="w-full h-40" />
          </div>
        )}

        <div className="flex justify-end mt-6">
          <LoadingButton
            disabled={!values?.file}
            type="submit"
            loading={loading}
            loadingMessage={loadingMessage}
            className="bg-gray-900 text-white px-8 py-3 rounded-lg hover:bg-gray-800 transition-colors font-medium disabled:opacity-50 disabled:cursor-not-allowed"
            onClick={() => Mixpanel.track(MIXPANEL_EVENTS.btn_create_purchase_invoice)}>
            {t("purchase_invoices.import_btn")}
          </LoadingButton>
        </div>
      </div>
      <GenerateEmailAddress open={openGenerateEmailAddress} setOpen={setOpenGenerateEmailAddress} />
    </form>
  );
};
export default CreatePurchaseInvoice;
